/**
 * Money labels for transaction Mixpanel events.
 *
 * Fee figures come from `protectedData.effectiveCommission` -- a snapshot the
 * server writes at initiate/transition time -- rather than from lineItems.
 * Reading `line-item/provider-commission` off lineItems (the legacy
 * `commission_amount` prop) gives the NOW fee only: it misses
 * `line-item/vat-tax-on-now-fee`, so it undercounts real platform revenue,
 * and it is in subunits while the snapshot is in majors.
 *
 * Units: payinTotal/payoutTotal are subunits (halalas); effectiveCommission
 * amounts are already SAR majors. Everything here is normalised to majors and
 * suffixed `_sar` so the two can never be summed together by accident.
 *
 * `user_id` is intentionally not returned -- call sites already pass their own.
 */

import { resolveBookedListingId } from '../components/CompleteOrder/completeOrderHelpers';
import { SERVICE_LISTING_TYPE } from '../util/types';

const toMajor = money =>
  Number.isFinite(money?.amount) ? money.amount / 100 : null;

const num = value =>
  value === null || value === undefined || value === '' || !Number.isFinite(Number(value))
    ? null
    : Number(value);

const round2 = n => Math.round(n * 100) / 100;

export const buildTransactionValueProps = (
  transaction,
  { role, listing: listingOverride, listingTitle: titleOverride } = { role: 'customer' }
) => {
  const { id, attributes, listing, provider, customer, relationships } = transaction ?? {};
  // Mobile passes a denormalised entity; the web duck passes the raw JSON:API
  // resource, where the parties live under `relationships` instead.
  const listingEntity = listingOverride ?? listing;
  const { payinTotal, payoutTotal, protectedData, processName } = attributes ?? {};
  const effectiveCommission = protectedData?.effectiveCommission;

  // Staff bookings attach the staff member's own listing as tx.listing /
  // relationships.listing -- resolveBookedListingId reads protectedData
  // (present in both the denormalised and raw shapes) to find the real
  // service listing instead. When it disagrees with the attached listing,
  // the attached one was the staff standing in for it: there's no included
  // entity for the real listing to read a title/type off, so title needs an
  // explicit override from a caller that fetched it separately.
  const rawListingId = listingEntity?.id?.uuid ?? relationships?.listing?.data?.id?.uuid ?? null;
  const bookedListingId = resolveBookedListingId(transaction);
  const isSubstituted = Boolean(bookedListingId && rawListingId && bookedListingId !== rawListingId);

  const nowFee = num(effectiveCommission?.nowFeeAmount);
  const vatOnNowFee = num(effectiveCommission?.vatOnNowFeeAmount);
  const platformFee =
    nowFee === null && vatOnNowFee === null ? null : round2((nowFee ?? 0) + (vatOnNowFee ?? 0));

  return {
    tx_id: id?.uuid ?? null,
    listing_id: bookedListingId ?? rawListingId,
    listing_title: titleOverride ?? (isSubstituted ? null : listingEntity?.attributes?.title ?? null),
    listing_type: isSubstituted
      ? SERVICE_LISTING_TYPE
      : listingEntity?.attributes?.publicData?.listingType ?? null,
    merchant_id: provider?.id?.uuid ?? relationships?.provider?.data?.id?.uuid ?? null,
    customer_id: customer?.id?.uuid ?? relationships?.customer?.data?.id?.uuid ?? null,
    process_name: processName ?? null,
    currency: payinTotal?.currency ?? payoutTotal?.currency ?? null,

    customer_paid_sar: toMajor(payinTotal),
    // payoutTotal is provider-only in Sharetribe; it is absent on the
    // customer's copy, so only claim it when tracking as the merchant.
    ...(role === 'merchant' ? { provider_amount_sar: toMajor(payoutTotal) } : {}),

    now_fee_sar: nowFee,
    vat_on_now_fee_sar: vatOnNowFee,
    platform_fee_sar: platformFee,
    merchant_commission_percent: num(effectiveCommission?.nowFeePercentage ?? effectiveCommission?.percent),
    merchant_commission_source: effectiveCommission?.source ?? null,
    merchant_commission_bucket: effectiveCommission?.bucketName ?? null,
  };
};

/**
 * Redemption is the moment `releasePayIn` runs server-side and the money
 * becomes the merchant's. The app has no transaction object at that point --
 * `/api/verify-qr-code` returns only a summary -- so these props come off the
 * response body instead of a Sharetribe entity.
 *
 * `merchantAmount` and the effectiveCommission amounts are already SAR majors
 * (the endpoint divides by 100), so nothing is converted here.
 */
export const buildRedemptionValueProps = response => {
  const effectiveCommission = response?.effectiveCommission;
  const nowFee = num(effectiveCommission?.nowFeeAmount);
  const vatOnNowFee = num(effectiveCommission?.vatOnNowFeeAmount);

  return {
    tx_id: response?.transactionId ?? null,
    process_name: response?.processName ?? null,
    merchant_id: response?.merchant_id ?? null,
    customer_id: response?.customer_id ?? null,
    provider_amount_sar: num(response?.merchantAmount),
    now_fee_sar: nowFee,
    vat_on_now_fee_sar: vatOnNowFee,
    platform_fee_sar:
      nowFee === null && vatOnNowFee === null ? null : round2((nowFee ?? 0) + (vatOnNowFee ?? 0)),
    merchant_commission_percent: num(effectiveCommission?.nowFeePercentage ?? effectiveCommission?.percent),
    merchant_commission_source: effectiveCommission?.source ?? null,
    merchant_commission_bucket: effectiveCommission?.bucketName ?? null,
  };
};
